<template>
  <div class="login-container">
    <div class="login-box">
      <h2 class="login-title">登录</h2>

      <div class="form-item">
        <label>用户名</label>
        <input v-model="form.userName" type="text" placeholder="请输入用户名" @keyup.enter="handleLogin"/>
      </div>

      <div class="form-item">
        <label>密码</label>
        <!-- 密码输入框：外层相对定位，右侧放“小眼睛”按钮切换明文/密文 -->
        <div class="password-wrap">
          <input
            v-model="form.userPassword"
            :type="showPassword ? 'text' : 'password'"
            placeholder="请输入密码"
            @keyup.enter="handleLogin"
          />
          <!-- 点击切换密码是否可见 -->
          <span class="password-eye" @click="showPassword = !showPassword">
            <!-- 可见状态：睁眼图标 -->
            <svg v-if="showPassword" viewBox="0 0 24 24">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <!-- 隐藏状态：闭眼图标（眼睛上加一道斜线） -->
            <svg v-else viewBox="0 0 24 24">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a19.77 19.77 0 0 1 4.22-5.06" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a19.87 19.87 0 0 1-3.17 4.19" />
              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </span>
        </div>
      </div>

      <!-- 记住我：勾选后下次进入登录页自动回填账号和密码 -->
      <div class="form-options">
        <label class="remember-label" @click.prevent="rememberMe = !rememberMe">
          <!-- 自定义方框：勾选时显示对勾 -->
          <span class="checkbox" :class="{ checked: rememberMe }">
            <svg v-if="rememberMe" viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span>记住我</span>
        </label>
      </div>

      <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>

      <button class="login-btn" :disabled="loading" @click="handleLogin">
        {{ loading ? '登录中...' : '登 录' }}
      </button>

      <div class="register-link" @click="goRegister">没有账号？去注册</div>
    </div>

    <!-- 备案信息固定在页面底部，点击跳转到工信部备案查询网站 -->
    <div class="beian-footer">
      <a
        href="https://beian.miit.gov.cn/"
        target="_blank"
        rel="noopener noreferrer"
        class="beian-link"
      >
        浙ICP备2025190764号
      </a>
    </div>
  </div>
</template>

<script>
import { login } from '@/api/user'

export default {
  name: 'UserLogin',
  data() {
    return {
      form: {
        userName: '',
        userPassword: ''
      },
      loading: false,
      errorMsg: '',
      rememberMe: false,    // 是否勾选“记住我”
      showPassword: false   // 密码是否明文显示（小眼睛切换）
    }
  },
  // 进入登录页时：读取上次保存的登录信息并回填
  created() {
    this.loadSavedLogin()
  },
  methods: {
    // 读取上次“记住我”保存的账号密码并回填；没勾选则保证密码框为空
    // localStorage 三个键：rememberFlag（'1'=已勾选）、rememberUsername、rememberPassword
    loadSavedLogin() {
      const remembered = localStorage.getItem('rememberFlag') === '1'
      if (remembered) {
        this.rememberMe = true
        this.form.userName = localStorage.getItem('rememberUsername') || ''
        this.form.userPassword = localStorage.getItem('rememberPassword') || ''
      } else {
        // 未勾选时不回填，同时清掉可能残留的账号密码，避免泄露
        this.rememberMe = false
        this.form.userName = ''
        this.form.userPassword = ''
      }
    },

    // 登录成功后按勾选情况保存或清除账号密码
    saveLoginIfNeeded() {
      if (this.rememberMe) {
        // 勾选：写入标记 + 账号 + 密码，下次进登录页自动回填
        localStorage.setItem('rememberFlag', '1')
        localStorage.setItem('rememberUsername', this.form.userName)
        localStorage.setItem('rememberPassword', this.form.userPassword)
      } else {
        // 未勾选：删除这三项，不保留任何登录信息
        localStorage.removeItem('rememberFlag')
        localStorage.removeItem('rememberUsername')
        localStorage.removeItem('rememberPassword')
      }
    },

    async handleLogin() {
      if (!this.form.userName || !this.form.userPassword) {
        this.errorMsg = '请输入用户名和密码'
        return
      }

      this.loading = true
      this.errorMsg = ''

      try {
        const res = await login(this.form)
        // 后端返回格式：{ code: "200", data: {"token": "xxx", "userRole": "manager","userName":"xxx"}, msg: null, success: true }
        if (res.code === '200' || res.success) {
          // data 直接是 token 字符串
          localStorage.setItem('token', res.data.token)
          localStorage.setItem('userRole', res.data.userRole)
          localStorage.setItem('userName', res.data.userName)
          // 登录成功后，根据“记住我”勾选情况保存或清除账号密码
          this.saveLoginIfNeeded()
          this.$router.push('/home')
        } else {
          this.errorMsg = res.msg || '登录失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    // 跳转到注册页
    goRegister() {
      this.$router.push('/register')
    }
  }
}
</script>

<style scoped>
.login-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 多层渐变叠加：底层斜向三色渐变 + 径向光晕营造层次感 */
  background:
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(118, 75, 162, 0.4) 0%, transparent 50%),
    linear-gradient(135deg, #667eea 0%, #6a8de0 35%, #764ba2 100%);
}

.login-box {
  width: 100%;
  max-width: 400px;
  padding: 40px 30px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(102, 126, 234, 0.1);
}

.login-title {
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
}

.form-item {
  margin-bottom: 20px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  color: #606266;
  font-size: 14px;
}

.form-item input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-item input:focus {
  border-color: #667eea;
}

/* ===== 密码可见切换（小眼睛） ===== */
.password-wrap {
  position: relative; /* 作为眼睛按钮的定位参照 */
}

/* 密码框右侧留出眼睛图标的位置，避免文字被挡住 */
.password-wrap input {
  padding-right: 38px;
}

.password-eye {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.password-eye svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: #909399;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* ===== 记住我 ===== */
.form-options {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.remember-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  user-select: none;
}

/* 自定义勾选方框 */
.checkbox {
  width: 16px;
  height: 16px;
  border: 1.5px solid #c0c4cc;
  border-radius: 3px;
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

/* 勾选状态：主题色底 + 白色对勾 */
.checkbox.checked {
  background: #667eea;
  border-color: #667eea;
}

.checkbox svg {
  width: 12px;
  height: 12px;
  fill: none;
  stroke: #fff;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.error-msg {
  margin-bottom: 16px;
  color: #f56c6c;
  font-size: 13px;
  text-align: center;
}

.login-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.login-btn:hover {
  opacity: 0.9;
}

.login-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 注册入口链接 */
.register-link {
  margin-top: 16px;
  text-align: center;
  color: #667eea;
  font-size: 13px;
  cursor: pointer;
}

.register-link:hover {
  text-decoration: underline;
}

/* ===== 备案信息：固定在页面底部居中 ===== */
.beian-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 12px;
  text-align: center;
  z-index: 10;
}

/* 备案链接：浅色文字（页面是深色渐变背景），点击下划线提示可点 */
.beian-link {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  text-decoration: none;
}

.beian-link:hover {
  text-decoration: underline;
  color: #fff;
}

/* 手机端适配 */
@media screen and (max-width: 480px) {
  .login-box {
    margin: 0 16px;
    padding: 30px 20px;
  }

  .form-item input {
    font-size: 16px; /* 防止 iPhone 聚焦时页面放大 */
  }
}
</style>
